import { computed, Directive, HostBinding, HostListener, inject, input, InputSignal, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TargetType } from '@libs/shared/models';
import { PageViewService } from '@libs/shared/services';
import { from, map, Observable, shareReplay, switchMap } from 'rxjs';
import { ActionLinkHandler } from './action-link-handler';
import { LinkRouterService } from './router';

interface ActionLinkModel {
  type?: string;
}

@Directive({
  selector: '[uiLink]',
})
export class LinkDirective<T = string> {
  public link: InputSignal<T> = input.required();
  public linkTarget: InputSignal<TargetType | undefined> = input();
  public activePathClass: Signal<string> = input('ui-link-active-path');
  public target: Signal<TargetType> = computed((): TargetType => this.linkTarget() ?? '_self');

  private readonly href$: Observable<string> = toObservable(this.link).pipe(
    map(link => this.getRawHref(link)),
    switchMap(rawHref => from(this.formatHref(rawHref))),
    shareReplay(1),
  );

  private readonly isActivePath$: Observable<boolean> = this.href$.pipe(
    switchMap(href => this.pageViewService.isActivePath(href)),
  );

  private readonly href: Signal<string> = toSignal(this.href$, { initialValue: '' });
  private readonly isActivePath: Signal<boolean> = toSignal(this.isActivePath$, { initialValue: false });
  private readonly actionHandler: ActionLinkHandler | null = inject(ActionLinkHandler, { optional: true });

  public constructor(
    protected readonly linkRouter: LinkRouterService,
    protected readonly pageViewService: PageViewService,
  ) {
  }

  @HostBinding('attr.href')
  public get attrHref(): string {
    return this.href();
  }

  @HostBinding('attr.target')
  public get attrTarget(): TargetType {
    return this.target();
  }

  @HostBinding('class')
  public get classActiveRoute(): string {
    return this.isActivePath() ? this.activePathClass() : '';
  }

  @HostListener('click', ['$event'])
  public onClick(event: MouseEvent): void {
    const type: string | undefined = (this.link() as unknown as ActionLinkModel)?.type;

    if (this.actionHandler?.handle(type)) {
      event.preventDefault();

      return;
    }

    event.preventDefault();

    if (this.href() !== '#') {
      this.linkRouter.navigate(this.href(), this.target());
    }
  }

  protected getRawHref(link: T): string | undefined {
    return String(link);
  }

  private async formatHref(href?: string): Promise<string> {
    if (!href) {
      return '#';
    }

    if (href.startsWith('mailto:') || href.startsWith('tel:')) {
      return href;
    }

    return this.linkRouter.formatHref(href);
  }
}
